import React, { Dispatch, SetStateAction, useState } from 'react';
import { isEqual } from 'lodash';

import {
  CreateUpdateSurveyDto,
  ResponseError,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { usePokCore } from '../../../common/hooks/usePokCore';
import Button from '../../../common/components/Button/Button';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { validate } from '../../../common/pokCore/contexts/SurveyContext';
import { SurveyEditorTabs } from '../../containers/SurveyEditorView/SurveyEditorView';
import { responseErrors } from '../../../common/pokCore/validation/responseErrors';
import { useNavigation } from '../../../common/navigation';
import ConfirmationButton from '../../../app/components/ConfirmationButton';

interface SurveyBasicDataControlsProps {
  survey: CreateUpdateSurveyDto;
  originalSurvey: CreateUpdateSurveyDto;
  surveyId?: string;
  setSurveyId: Dispatch<SetStateAction<string | undefined>>;
  setNotSavedTab: (tab: SurveyEditorTabs | undefined) => void;
  setSaveErrors: Dispatch<SetStateAction<string[]>>;
  setShowSaveErrors: Dispatch<SetStateAction<boolean>>;
  setOriginalSurvey: Dispatch<SetStateAction<CreateUpdateSurveyDto>>;
  setActiveTab: Dispatch<SetStateAction<string>>;
  refreshSurvey: () => Promise<void>;
}

export const SurveyBasicDataControls: React.FC<
  SurveyBasicDataControlsProps
> = props => {
  const nav = useNavigation();
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const notifications = useNotifications();
  const pok = usePokCore();
  const { t, tk } = useTranslation('administrationLng');

  const editSurvey = async (surveyCU: CreateUpdateSurveyDto, id?: string) => {
    const status = await validate(surveyCU);
    if (status.valid) {
      try {
        let savedSurveyId = id;
        if (id) {
          await pok.surveys.update(id, surveyCU);
        } else {
          const savedSurvey = await pok.surveys.create(surveyCU);
          savedSurveyId = savedSurvey.id;
          props.setSurveyId(savedSurvey.id);
        }
        props.setOriginalSurvey(surveyCU);
        props.setNotSavedTab(undefined);
        await props.refreshSurvey();
        return {
          saved: true,
          errors: [],
          surveyId: savedSurveyId,
        };
      } catch (response) {
        return {
          saved: false,
          errors: await responseErrors(response as ResponseError),
        };
      }
    } else {
      return {
        saved: false,
        errors: status.errors,
      };
    }
  };

  const handleSaveAndNext = async () => {
    setIsSaveLoading(true);
    props.setSaveErrors([]);
    props.setShowSaveErrors(false);

    const hasChanges = !isEqual(props.survey, props.originalSurvey);
    const isNewSurvey = !props.surveyId;

    if (hasChanges || isNewSurvey) {
      const result = await editSurvey(props.survey, props.surveyId);

      if (!result.saved) {
        props.setSaveErrors(result.errors);
        props.setShowSaveErrors(true);
        setIsSaveLoading(false);
        return;
      }

      notifications.saveCompleted();

      // Jeśli to była nowa ankieta, przekieruj na URL z ID
      if (isNewSurvey && result.surveyId) {
        void nav.changeSurveyEditorPath(result.surveyId);
      }
    }

    props.setActiveTab(SurveyEditorTabs.QUESTIONS);
    setIsSaveLoading(false);
  };

  const isStartDateTodayOrEarlier = () => {
    if (!props.survey.startDate) {
      return false;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(props.survey.startDate);
    startDate.setHours(0, 0, 0, 0);
    return startDate <= today;
  };

  const isStartDateChanged = () => {
    const newDate = props.survey.startDate
      ? new Date(props.survey.startDate).setHours(0, 0, 0, 0)
      : null;
    const originalDate = props.originalSurvey.startDate
      ? new Date(props.originalSurvey.startDate).setHours(0, 0, 0, 0)
      : null;
    return newDate !== originalDate;
  };

  const isEndDateChanged = () => {
    const newDate = props.survey.endDate
      ? new Date(props.survey.endDate).setHours(0, 0, 0, 0)
      : null;
    const originalDate = props.originalSurvey.endDate
      ? new Date(props.originalSurvey.endDate).setHours(0, 0, 0, 0)
      : null;
    return newDate !== originalDate;
  };

  const isEndDateTodayOrLater = () => {
    if (!props.survey.endDate) {
      return true;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = new Date(props.survey.endDate);
    endDate.setHours(0, 0, 0, 0);
    return endDate >= today;
  };

  const needsConfirmationStartDate =
    props.survey.active && isStartDateTodayOrEarlier() && isStartDateChanged();

  const needsConfirmationWhenEndDate =
    props.survey.active &&
    isStartDateTodayOrEarlier() &&
    isEndDateChanged() &&
    isEndDateTodayOrLater();
  return (
    <div className="ms-auto d-flex gap-2">
      {needsConfirmationStartDate || needsConfirmationWhenEndDate ? (
        <ConfirmationButton
          variant="primary"
          modalSize="lg"
          onOK={handleSaveAndNext}
          isLoading={isSaveLoading}
          confirmation={
            needsConfirmationStartDate
              ? t(tk.survey.confirmStartSurveyMessage)
              : t(tk.survey.confirmEndSurveyMessage)
          }
        >
          {t(tk.survey.saveAndContinue)}
        </ConfirmationButton>
      ) : (
        <Button
          variant="primary"
          onClick={handleSaveAndNext}
          isLoading={isSaveLoading}
        >
          {t(tk.survey.saveAndContinue)}
        </Button>
      )}
    </div>
  );
};
